export default {
  kameraplaner: {
    intro: {
      badge: 'Interactive planning tool – free & non-binding',
      title: 'Plan your camera system in advance',
      lead:
        'With the SSG Camera Planner you can draw rooms or areas, mark doors, windows and important surveillance zones and test possible camera positions. You get an initial visual assessment that can then be professionally reviewed by SSG Signaltechnik.',
      noReg: 'No registration',
      localSave: 'Saved locally in your browser',
      noBinding: 'No binding number of cameras',
      quickTitle: 'Start quick check',
      quickDesc:
        'Answer questions about your property, the desired surveillance areas and functions in five short steps. You immediately get a clear summary.',
      quickList: {
        '0': 'Property type & areas to monitor',
        '1': 'Desired functions & property data',
        '2': 'Summarise the result & send directly',
      },
      quickCta: 'Start quick check',
      plannerTitle: 'Plan the floor plan yourself',
      plannerDesc:
        'Draw rooms and walls, place doors, windows, obstacles and cameras – with field-of-view cones, coverage analysis and automatic position suggestions.',
      plannerList: {
        '0': 'Drawing surface with grid & metre scale',
        '1': 'Field-of-view cones, surveillance targets & privacy zones',
        '2': 'Floor plan upload, evaluation & print',
      },
      plannerCta: 'Plan the floor plan yourself',
      savedTitle: 'There is a saved plan',
      savedDesc: 'It was saved locally automatically and can be continued.',
      resume: 'Continue last plan',
    },
    quick: {
      steps: { '0': 'Property type', '1': 'Areas', '2': 'Functions', '3': 'Property data', '4': 'Result' },
      back: 'Back to overview',
      title: 'Quick check',
      subtitle: 'Five short steps – afterwards you receive a clear summary of your preliminary plan.',
      stepOf: 'Step {{current}} of {{total}}',
      objectQuestion: 'Which property type suits you?',
      chooseOne: 'Please select an option.',
      areasQuestion: 'Which areas should be monitored?',
      multiSelect: 'Multiple selection possible.',
      featuresTitle: 'Desired functions',
      dataTitle: 'Property data',
      dataOptional: 'All details are optional and can be skipped.',
      areaSize: 'Approximate total area (m²)',
      roomCount: 'Number of rooms or areas',
      inOut: 'Indoor, outdoor or both?',
      inside: 'Indoor',
      outside: 'Outdoor',
      both: 'Both',
      maxDistance: 'Estimated greatest monitoring distance',
      pleaseSelect: 'Please select',
      distanceUpTo5: 'Up to 5 metres',
      distance5to10: '5 to 10 metres',
      distance10to20: '10 to 20 metres',
      distanceOver20: 'Over 20 metres',
      internet: 'Internet connection available?',
      network: 'Network cabling available?',
      power: 'Power at the mounting point?',
      yes: 'Yes',
      no: 'No',
      unknown: 'Unknown',
      recordingDuration: 'Desired recording duration',
      installType: 'New system or existing system?',
      newSystem: 'New system',
      existingSystem: 'Existing system',
      existingManufacturer: 'Manufacturer and model of the existing system (if known)',
      requirements: 'Special requirements',
      requirementsPlaceholder: 'For example special lighting conditions, heritage protection, special conditions...',
      chars: '{{current}}/500 characters',
      ssgCarePre: 'I am also interested in',
      ssgCarePost: '– maintenance and personal support for the system.',
      overviewTitle: 'Your preliminary plan at a glance',
      amberNote:
        'Based on your information, several camera positions or camera types may be suitable. For a reliable recommendation, viewing angles, mounting height, lighting and existing obstacles should be checked.',
      planFloorplan: 'Additionally plan floor plan',
      sendToSsg: 'Send details to SSG',
      editResult: 'Edit result',
      backBtn: 'Back',
      next: 'Next',
      summary: {
        objectType: 'Property type',
        areas: 'Surveillance areas',
        features: 'Desired functions',
        areaSize: 'Approximate total area',
        roomCount: 'Rooms / areas',
        inOut: 'Area',
        maxDistance: 'Greatest monitoring distance',
        internet: 'Internet connection',
        network: 'Network cabling',
        power: 'Power at mounting point',
        recordingDuration: 'Recording duration',
        installType: 'System',
        existingSystem: 'Existing system',
        requirements: 'Special requirements',
        ssgCare: 'SSG Care support',
      },
      values: {
        notSpecified: 'Not specified',
        inside: 'Indoor',
        outside: 'Outdoor',
        inOut: 'Indoor & outdoor',
        present: 'Available',
        notPresent: 'Not available',
        unknown: 'Unknown',
        unclear: 'still unclear',
        newSystem: 'New system',
        existing: 'Existing system',
        desired: 'Desired',
      },
    },
    topbar: {
      projectName: 'Project name',
      undo: 'Undo',
      undoTip: 'Undo (Ctrl+Z)',
      redo: 'Redo',
      redoTip: 'Redo (Ctrl+Y)',
      save: 'Save temporarily',
      saved: 'Saved',
      saveTip: 'Save temporarily',
      reset: 'Reset',
      resetTip: 'Reset planner',
      help: 'Help',
      helpTip: 'Open introduction again',
      evaluate: 'Evaluate plan',
      evaluateTip: 'Evaluate plan',
    },
    tools: {
      select: { label: 'Select', hint: 'Move, resize, rotate elements. Drag empty space = move.' },
      erase: { label: 'Delete', hint: 'Delete element by clicking.' },
      room: { label: 'Rectangular room', hint: 'Draw a room or area by dragging – dimensions are shown live.' },
      polygon: { label: 'Free area', hint: 'Draw a free area by hand: hold the mouse button and draw the shape.' },
      wall: { label: 'Wall', hint: 'Draw a wall by dragging – limits lines of sight.' },
      door: { label: 'Door', hint: 'Place a door on a wall – then move or rotate directly.' },
      window: { label: 'Window', hint: 'Place a window on a wall – then move or rotate directly.' },
      obstacle: { label: 'Obstacle', hint: 'Draw a column, shelf, partition or furniture block.' },
      watch: { label: 'Surveillance target', hint: 'Mark an important area that should be captured.' },
      privacy: { label: 'Privacy zone', hint: 'Mark an area that should not be captured.' },
      camera: { label: 'Camera', hint: 'Place a camera by clicking. The model dropdown opens on the right – the model matching the area is already recommended.' },
      text: { label: 'Text', hint: 'Add room names or labels.' },
    },
    editor: {
      tools: 'Tools',
      autoSuggest: 'Automatic suggestions',
      analysis: 'Coverage analysis',
      properties: 'Properties',
      backToIntro: 'Back to Camera Planner overview',
      resetTitle: 'Reset planner?',
      resetText:
        'All drawn elements, cameras and the uploaded floor plan will be permanently deleted. The saved plan will be removed.',
      cancel: 'Cancel',
      confirmReset: 'Yes, reset',
      close: 'Close',
      openTools: 'Open tools',
      openAnalysis: 'Open analysis',
      snap: 'Toggle snap to grid',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      fit: 'Centre floor plan',
      closeHints: 'Close hints',
    },
    canvas: {
      areaLabel: 'Floor plan drawing surface',
      calibHint: 'Please set a distance for scaling',
      locked: 'locked',
      scale: 'Scale: approx. {{px}} px/m · Grid {{step}} m',
      calibNotice: 'Click two points on the floor plan whose distance you know.',
    },
    props: {
      edit: 'Edit properties',
      delete: 'Delete',
      cameraModel: 'Camera model (real device)',
      nameNumber: 'Name / number',
      cameraCategory: 'Camera category',
      modelAssigned: 'Model "{{series}}" is assigned.',
      planningValues: 'These are planning values – not technical data of a real product.',
      visibility: 'Visibility',
      recognition: 'Recognition',
      detail: 'Detail',
      rotation: 'Orientation (°)',
      fov: 'Viewing angle (°)',
      range: 'Range (m)',
      posX: 'Position X (m)',
      posY: 'Position Y (m)',
      mountHeight: 'Mounting height (m)',
      indoor: 'Indoor',
      outdoor: 'Outdoor',
      usage: 'Area of use',
      dayNight: 'Day / night',
      target: 'Desired surveillance target',
      noneAssigned: 'None assigned',
      targetArea: 'Target area',
      remark: 'Note',
      remarkPlaceholder: 'e.g. notes on mounting or surroundings...',
      coneNote:
        'The field-of-view cone is a planning representation. Actual image quality depends on the model, lens, distance, mounting height, light and on-site obstacles.',
      roomLabel: 'Room designation',
      length: 'Length (m)',
      width: 'Width (m)',
      area: 'Area: approx. {{area}} m²',
      designation: 'Designation',
      points: '{{count}} points – move points directly on the drawing surface.',
      wallThickness: 'Wall thickness (m)',
      wallLength: 'Length: approx. {{length}} m',
      wallNote: 'Solid walls limit lines of sight in the planner.',
      obstacleKind: 'Type of obstacle',
      shape: 'Shape',
      rect: 'Rectangle',
      circle: 'Circle',
      caption: 'Label',
      diameter: 'Diameter (m)',
      depth: 'Depth (m)',
      goalKind: 'Type of target',
      priority: 'Priority',
      goal: 'Desired target',
      coverageNote: 'Coverage is rated by colour (green / yellow / red). Stricter requirements apply to detail targets.',
      zoneKind: 'Type of zone',
      privacyNote: 'If a field-of-view cone overlaps this zone, a warning appears in the analysis.',
      textContent: 'Text content',
      duplicate: 'Duplicate',
      deleteElement: 'Delete element',
      projectData: 'Project data',
      noElement: 'No element selected',
      projectName: 'Project name',
      objectType: 'Property type',
      objectTypePlaceholder: 'e.g. single-family home',
      projectDescription: 'Project description',
      projectDescriptionPlaceholder: 'Special features, wishes or general conditions...',
      maxChars: 'Maximum 500 characters',
      cameraForNew: 'Camera model for new cameras',
      cameraForNewDesc:
        'Select the desired Safire model below – the dropdown opens automatically. New cameras are placed with these settings.',
      recommendedFor: 'Recommended for your area:',
      orGeneric: 'or generic category',
      tips: 'Tips',
      tip1: 'Drag empty space with the selection tool = move drawing surface. Mouse wheel = zoom.',
      tip2: 'Ctrl+Z = undo, Ctrl+Y = redo, Del = delete element.',
      tip3: 'Snap to grid can be toggled in the view options.',
      suggestPositions: 'Suggest camera positions automatically',
      doorSwing: 'Opening direction',
      left: 'Left',
      right: 'Right',
      none: 'None',
      attached: 'Placed on a wall – drag along the wall to move. Rotating releases the element.',
      detached: 'Placed free-standing – move and rotate freely.',
      doorNote: 'Material, glazing and opening state must be checked on site.',
    },
    result: {
      edit: 'Edit plan',
      print: 'Print summary',
      savePdf: 'Save as PDF',
      send: 'Send plan to SSG',
      eyebrow: 'Evaluation',
      title: 'Your preliminary plan at a glance',
      disclaimer:
        'This evaluation is a non-binding preliminary plan. A final recommendation is only made after checking the local conditions and selecting the specific camera technology.',
      project: 'Project',
      objectType: 'Property type:',
      uploadPresent: 'Floor plan upload available:',
      scaled: 'scaled',
      refDistance: 'm reference distance',
      notScaled: 'not yet scaled',
      createdAt: 'Created on',
      floorplan: 'Drawn floor plan',
      noElements: 'No elements in the floor plan yet.',
      stats: {
        rooms: 'Rooms & areas',
        walls: 'Walls',
        doors: 'Doors',
        windows: 'Windows',
        obstacles: 'Obstacles',
        cameras: 'Cameras',
        zones: 'Target areas',
        privacy: 'Privacy zones',
      },
      zonesTitle: 'Surveillance targets and priorities',
      noZones: 'No surveillance targets marked.',
      priorityLabel: 'Priority',
      goalLabel: 'Target',
      goalOverview: 'Overview',
      goalDetail: 'Detail',
      goalObservation: 'Observation',
      coverage: 'geometric coverage',
      camerasTitle: 'Camera positions and camera types',
      noCameras: 'No cameras placed yet.',
      modelLabel: 'Model',
      fovLabel: 'Viewing angle',
      visibilityLabel: 'Visibility approx.',
      recognitionLabel: 'Recognition approx.',
      detailLabel: 'Detail approx.',
      positionLabel: 'Position',
      rotationLabel: 'Orientation',
      rangeLabel: 'Range approx.',
      mountLabel: 'Mounting height',
      indoor: 'Indoor',
      outdoor: 'Outdoor',
      day: 'Day',
      night: 'Night',
      dayNight: 'Day & night',
      target: 'Target',
      assigned: 'Assigned',
      modelNote:
        'Selected Safire models are based on datasheet values; visibility/recognition/detail are calculated planning values (EN 62676-4). Without a model, values are generic planning values – not technical data of a real product.',
      blindTitle: 'Possible blind spots and open points',
      uncovered: 'Areas not sufficiently covered:',
      privacyOverlaps: 'Overlaps with privacy zones:',
      checkPrivacy: 'Check position, orientation or masking.',
      missingTitle: 'Missing important details',
      quickTitle: 'Desired functions and property data',
      areasLabel: 'Areas:',
      featuresLabel: 'Functions:',
      areaLabel: 'Area:',
      recordingLabel: 'Recording:',
      internetLabel: 'Internet:',
      powerLabel: 'Power:',
      careInterest: 'Interest in SSG Care support',
      finalNote:
        'The plan will be professionally reviewed by SSG Signaltechnik. Mounting height, lighting, viewing angles and on-site conditions determine the final implementation.',
      sendBottom: 'Send plan to SSG Signaltechnik',
      consult: 'Request personal consultation',
      good: 'Well covered',
      partial: 'Partially covered',
      insufficient: 'Not sufficient',
    },
    send: {
      back: 'Back',
      title: 'Send plan to SSG Signaltechnik',
      lead:
        'Your planner data (floor plan, cameras, viewing angles, surveillance targets, privacy zones and analysis result) is transmitted together with your contact details. Contact details are only needed now – the plan itself remains locally on your device.',
      dataTitle: 'Transmitted planner data',
      floorplan: 'Floor plan',
      rooms: 'Rooms/areas',
      walls: 'Walls',
      doors: 'Doors',
      windows: 'Windows',
      obstacles: 'Obstacles',
      cameras: 'Cameras',
      none: 'none',
      zones: 'Surveillance targets',
      privacy: 'Privacy zones',
      analysis: 'Analysis',
      good: 'good',
      partial: 'partial',
      insufficient: 'not sufficiently covered',
      upload: 'Floor plan upload',
      scaled: 'scaled',
      notScaled: 'not scaled',
      customerType: 'Private or business customer *',
      privateCustomer: 'Private customer',
      businessCustomer: 'Business customer',
      name: 'First and last name *',
      company: 'Company (optional)',
      phone: 'Phone number',
      email: 'Email address *',
      projectAddress: 'Project address',
      projectAddressPlaceholder: 'Street, postal code, city',
      contactPref: 'Preferred contact method',
      any: 'Any',
      byPhone: 'Phone',
      byEmail: 'Email',
      callbackTime: 'Preferred callback time',
      noCallback: 'No callback desired',
      morning: 'Morning',
      afternoon: 'Afternoon',
      evening: 'Evening',
      systemStatus: 'New or existing system',
      newSystem: 'New system',
      existingSystem: 'Existing system',
      unsure: 'Still unsure',
      existingManufacturer: 'Manufacturer of an existing system (if known)',
      existingManufacturerPlaceholder: 'e.g. Hikvision, Safire...',
      careInterest: 'Interest in SSG Care',
      careYes: 'Yes, please inform me',
      careNo: 'No',
      careLater: 'Decide later',
      message: 'Additional message',
      messagePlaceholder: 'Additions to your plan...',
      fileUpload: 'Floor plan / photos (optional)',
      fileNote: 'Note: file uploads are treated as "Uncollectable" – the planning data is transmitted as text.',
      privacyConsent:
        'I have read the privacy policy and agree to the processing of my data for handling this request. The planning data is used exclusively for processing. *',
      privacyLink: 'Privacy policy',
      error: 'An error occurred. Please try again later.',
      networkError: 'Network error. Please check your connection and try again.',
      successTitle: 'Thank you for your plan.',
      successText: 'SSG Signaltechnik will review your details and contact you personally.',
      sending: 'Sending...',
      submit: 'Send plan to SSG',
      backToResult: 'Back to evaluation',
    },
    tour: {
      stepOf: 'Step {{current}} of {{total}}',
      skip: 'Skip',
      back: 'Back',
      next: 'Next',
      finish: "Let's go",
      dialogLabel: 'Camera Planner introduction',
      steps: {
        '0': { title: 'Welcome to the SSG Camera Planner', text: 'Draw your room or upload a floor plan.' },
        '1': { title: 'Mark areas', text: 'Mark doors, windows and important areas.' },
        '2': { title: 'Place cameras', text: 'Place cameras or have positions suggested.' },
        '3': { title: 'Check fields of view', text: 'Check fields of view and possible blind spots.' },
        '4': { title: 'Send plan', text: 'Send the preliminary plan to SSG Signaltechnik.' },
      },
    },
    analysis: {
      title: 'Coverage analysis',
      suggest: 'Suggest positions',
      suggestTip: 'Suggest camera positions',
      cameras: 'Cameras',
      zones: 'Target areas',
      good: 'Well covered',
      partial: 'Partial',
      notCovered: 'Not covered',
      privacyConflict: 'Privacy zone conflict',
      areaCoverage: 'Geometric area coverage',
      areaNote: 'Geometrically represented coverage – no statement about actual image or recognition quality.',
      legend: 'Legend',
      legendFov: 'Camera field of view',
      legendDetail: 'Sharp detail recognition (dashed)',
      legendGood: 'Target well covered',
      legendPartial: 'Target partially covered',
      legendBad: 'Target insufficient',
      legendPrivacy: 'Privacy zone / excluded',
      note: 'The colour rating is a geometric orientation. A wide viewing angle does not automatically mean sufficient detail recognition – distance, resolution and mounting height must be checked.',
    },
    upload: {
      title: 'Upload floor plan',
      desc: 'Optional: load an existing floor plan (JPG, PNG or WebP) as the background. Then mark a known distance and enter its length so the planner scales correctly.',
      choose: 'Select floor plan',
      chooseOther: 'Choose another image',
      remove: 'Remove',
      calibPrompt: 'Now click point A and point B on the floor plan in the drawing surface.',
      startCalib: 'Start scaling',
      scaled: 'Scaled: {{cm}} cm per pixel · Distance {{m}} m',
      lengthPlaceholder: 'Length in metres',
      apply: 'Apply',
      opacity: 'Opacity {{pct}} %',
      lock: 'Lock background (prevents accidental movement)',
      moveHint: 'To move: drag an empty spot on the image with the selection tool (if not locked).',
      privacyNote: 'Floor plans and property photos can contain sensitive information. Only upload files that are required to process your request.',
      onlyImages: 'Please select only JPG, PNG or WebP images.',
      tooLarge: 'The file is too large. Please select an image under 8 MB.',
      readError: 'The file could not be read.',
      validLength: 'Please enter a valid length in metres.',
      tooClose: 'The marked points are too close together.',
    },
    picker: {
      generic: 'Select generic category',
      genericOption: 'Generic category (without model)',
      recommended: 'Recommended',
      recommendation: 'Recommendation for your area',
      fovLabel: 'Viewing angle',
      detailFrom: 'Detail from approx. {{detail}} m · Recognition approx. {{recognition}} m',
      hFov: 'Horizontal viewing angle',
      vFov: 'Vertical viewing angle',
      resolution: 'Resolution',
      lens: 'Lens',
      irRange: 'IR range',
      ipClass: 'Protection class',
      audio: 'Audio',
      dayNight: 'Day / night',
      datenblatt: 'Datasheet',
      planungswert: 'Planning value',
      doriNote:
        'Visibility/recognition/detail: calculated planning values according to EN 62676-4 (pixel density). Actual image quality depends on the model, lens, mounting height, light and on-site obstacles.',
      groups: {
        standard: 'Standard / indoor & outdoor',
        outdoor: 'Outdoor',
        panorama180: 'Panorama',
        ptz: 'Pan / zoom',
      },
    },
    quickswitch: {
      title: 'Change camera model',
      subtitle: 'Switch variant directly',
      active: 'Active',
      recommended: 'Recommended',
      note: 'Set orientation, range and mounting height in the right-hand properties panel.',
      close: 'Close model selection',
    },
    elementTypes: {
      room: 'Room / area',
      polygon: 'Free area',
      wall: 'Wall',
      door: 'Door',
      window: 'Window',
      obstacle: 'Obstacle',
      watchzone: 'Surveillance target',
      privacy: 'Privacy zone / exception',
      camera: 'Camera',
      text: 'Text',
    },
    defaults: { room: 'Room', watchzone: 'Target area', text: 'Room name' },
    defaultProject: 'My camera plan',
    quickProject: 'Camera planning (quick check)',
    options: {
      objectTypes: {
        Einfamilienhaus: 'Single-family home',
        Mehrfamilienhaus: 'Apartment building',
        Wohnung: 'Apartment',
        'Ladengeschäft': 'Retail store',
        'Büro oder Praxis': 'Office or practice',
        Gastronomie: 'Hospitality',
        'Lager oder Logistik': 'Warehouse or logistics',
        Werkstatt: 'Workshop',
        'Parkplatz oder Außenfläche': 'Parking lot or outdoor area',
        Baustelle: 'Construction site',
        Sonstiges: 'Other',
      },
      areas: {
        Haupteingang: 'Main entrance',
        Nebeneingang: 'Side entrance',
        Fenster: 'Window',
        Einfahrt: 'Driveway',
        Garage: 'Garage',
        'Garten oder Terrasse': 'Garden or terrace',
        Kasse: 'Checkout',
        Verkaufsfläche: 'Sales floor',
        Lager: 'Warehouse',
        Lieferzone: 'Delivery zone',
        Parkplatz: 'Parking lot',
        'Büro oder Innenraum': 'Office or interior',
        'Gesamte Außenfläche': 'Entire outdoor area',
        'Sonstiger Bereich': 'Other area',
      },
      features: {
        'Live-Ansicht': 'Live view',
        'lokale Aufzeichnung': 'Local recording',
        'Smartphone-Zugriff': 'Smartphone access',
        'Wiedergabe gespeicherter Aufnahmen': 'Playback of saved recordings',
        Ereignisbenachrichtigungen: 'Event notifications',
        'gute Sicht bei Dunkelheit': 'Good visibility in the dark',
        'Übersicht über eine große Fläche': 'Overview of a large area',
        'Erkennung wichtiger Details': 'Recognition of important details',
        'mehrere Benutzer': 'Multiple users',
        'mehrere Standorte': 'Multiple locations',
        'vorhandene Anlage erweitern': 'Expand existing system',
        'SSG-Care-Betreuung': 'SSG Care support',
      },
      recordingDurations: {
        'Bis 7 Tage': 'Up to 7 days',
        'Bis 14 Tage': 'Up to 14 days',
        'Bis 30 Tage': 'Up to 30 days',
        'Mehr als 30 Tage': 'More than 30 days',
        'noch unklar': 'still unclear',
      },
      roomNames: {
        Eingang: 'Entrance',
        Flur: 'Hallway',
        Wohnzimmer: 'Living room',
        'Büro': 'Office',
        Verkaufsfläche: 'Sales floor',
        Kasse: 'Checkout',
        Lager: 'Warehouse',
        Werkstatt: 'Workshop',
        Parkplatz: 'Parking lot',
        Außenbereich: 'Outdoor area',
      },
      obstacles: {
        saeule: 'Column',
        regal: 'Shelf',
        trennwand: 'High partition',
        moebel: 'Furniture block',
        fahrzeug: 'Vehicle parking space',
        eigen: 'Named obstacle',
      },
      watchKinds: {
        ueberblick: 'Fully overlook the area',
        eingang: 'Observe entrance',
        tuer: 'Monitor door',
        fenster: 'Monitor window',
        kasse: 'Capture checkout or sensitive point',
        zufahrt: 'Observe driveway',
        kennzeichen: 'Check licence plate area',
        flaeche: 'Overview of an area',
        details: 'Recognise important details',
        individuell: 'Individual area',
      },
      watchPriorities: {
        normal: 'Normal',
        wichtig: 'Important',
        kritisch: 'Critical',
      },
      watchGoals: {
        uebersicht: 'Overview',
        beobachtung: 'Observation',
        detail: 'Detail',
      },
      privacyKinds: {
        privat: 'Privacy zone',
        nachbar: 'Neighbouring property',
        oeffentlich: 'Public walkway or street',
        mitarbeiter: 'Employee area',
        ausgeschlossen: 'Area not to be captured',
      },
      categories: {
        standard: { label: 'Standard camera', hint: 'Versatile for indoor and outdoor areas' },
        wide: { label: 'Wide-angle camera', hint: 'Broad overview at short to medium distance' },
        varifocal: { label: 'Camera with variable viewing angle', hint: 'Viewing angle is set on site' },
        panorama180: { label: '180° panorama camera', hint: 'Very wide capture, limited detail recognition' },
        fisheye360: { label: '360° / fisheye camera', hint: 'All-round view, image is electronically corrected' },
        ptz: { label: 'PTZ camera', hint: 'Pan/tilt/zoom camera for larger areas' },
        indoor: { label: 'Indoor camera', hint: 'Optimised for indoor areas' },
        outdoor: { label: 'Outdoor camera', hint: 'Weather-resistant version for outdoors' },
      },
      dayNight: {
        tag: 'Day',
        nacht: 'Night',
        beides: 'Day and night',
      },
    },
    models: {
      'safire-turret-i1-4mp': {
        ai: 'Person & vehicle classification (AI)',
        note: 'Versatile turret camera with a wide viewing angle – a good standard for indoors and outdoors.',
      },
      'safire-turret-e1-4mp': {
        ai: 'Person & vehicle classification (AI)',
        note: 'Affordable entry-level model of the E1 series with solid resolution.',
      },
      'safire-turret-b1-4mp': {
        ai: '',
        note: 'Robust economy turret with impact-resistant housing (IK10).',
      },
      'safire-dome-i2-8mp': {
        ai: 'Person & vehicle classification (AI)',
        note: 'High 8 MP resolution – more detail in the same image section.',
      },
      'safire-bullet-4mp': {
        ai: 'Person & vehicle classification (AI)',
        note: 'Classic bullet design for outdoor mounting. Viewing angle as a planning value based on the series.',
      },
      'safire-turret-nightcolor-4mp': {
        ai: 'Colour image in the dark (NightColor X)',
        note: 'Delivers a colour image even in low light – ideal for critical night situations. Viewing angle as a planning value.',
      },
      'safire-turret-duallight-4mp': {
        ai: 'Person & vehicle classification (AI)',
        note: 'DualLight with IR and white-light LED (30 m each) – flexible at night. Viewing angle as a planning value.',
      },
      'safire-panorama-180-8mp': {
        ai: '180° all-round view with speaker & microphone',
        note: 'Captures a very wide area with one camera – detail recognition at distance is limited.',
      },
      'safire-miniptz-4mp': {
        ai: 'Autotracking, person & vehicle classification',
        note: 'Pan/tilt/zoom camera with autotracking. Viewing angle as a planning value (wide-angle end position).',
      },
      'safire-ptz-15x-2mp': {
        ai: 'Autotracking, car & vehicle tracking',
        note: 'Long range thanks to 15× zoom and 200 m IR. Viewing angle as a planning value (wide-angle end position).',
      },
    },
    disclaimer: {
      general:
        'The Camera Planner provides a non-binding preliminary plan. The actual image section and suitable camera position depend, among other things, on the camera model, lens, distance, mounting height, lighting, obstacles and on-site conditions.',
      result:
        'This evaluation is a non-binding preliminary plan. A final recommendation is only made after checking the local conditions and selecting the specific camera technology.',
      upload:
        'Floor plans and property photos can contain sensitive information. Only upload files that are required to process your request.',
      sichtfeld:
        'Doors and windows can affect lines of sight. Material, glazing, opening state and light reflection must be checked on site.',
      datenschutz:
        'For requirements such as facial recognition, person identification or licence plate recognition, a technical and data protection review is required.',
    },
    hints: {
      '0':
        'Based on your information, several camera positions or camera types may be suitable. For a reliable recommendation, viewing angles, mounting height, lighting and existing obstacles should be checked.',
      '1':
        'A wide viewing angle does not automatically mean sufficient detail recognition. Distance, resolution, viewing angle, mounting height and the surveillance target influence the actual image quality.',
      '2': 'Mounting height and lighting conditions must be checked on site.',
    },
    recommend: {
      noArea:
        'Standard recommendation as long as no area has been drawn yet. Draw your room and the recommendation will adjust.',
      veryLarge: 'Very large area (approx. {{area}} m²) – PTZ with zoom and long range for wide distances.',
      large: 'Large area (approx. {{area}} m²) – robust bullet design for outdoor areas.',
      medium: 'Medium to large area (approx. {{area}} m²) – 8 MP for more detail across the width.',
      compact: 'Compact area (approx. {{area}} m²) – a wide viewing angle covers the room well.',
    },
    analysisDynamic: {
      missingNoCameras: 'No cameras placed yet.',
      missingNoRooms: 'No rooms or areas drawn yet.',
      missingNoWalls: 'No walls drawn – field-of-view cones are not limited.',
      missingNoZones: 'No surveillance targets marked.',
      missingNoPrivacy: 'No privacy or exclusion zones marked.',
      missingNoInternet: 'Information about internet connection is missing.',
      missingNoPower: 'Information about power supply is missing.',
      hintUncovered: 'Target areas without sufficient coverage: check camera positions or plan additional cameras.',
      hintPartial: 'Some target areas are only partially covered – adjust viewing angle, position or range.',
      hintDetailRange:
        'The detail range of "{{name}}" (approx. {{range}} m) is probably not sufficient for large rooms alone – an additional camera could be useful.',
      hintPrivacy: 'The field of view overlaps with a marked privacy zone. Camera position, orientation or technical masking should be checked.',
      hintMount: 'Mounting height and lighting conditions must be checked on site.',
      hintKennzeichen: 'Technical and data protection review required.',
      noteDetailRange:
        'Detail recognition is probably not sufficient for approx. {{distance}} m distance – detail max. approx. {{identification}} m. A narrower viewing angle or a model with higher resolution should be checked.',
      noteDetailUncertain:
        'Detail recognition over longer distances is uncertain – a narrower viewing angle and mounting height should be checked.',
      notePartial: 'This area may only be partially covered.',
      suggestNear:
        'Surveillance target "{{target}}" is close to "{{privacy}}". Position and orientation must be chosen carefully – data protection review required.',
      suggestHeuristic:
        'The suggestions are heuristic. Every camera can be freely moved, rotated, changed or deleted.',
      suggestWide:
        'A wide viewing angle does not mean sufficient detail recognition. Distance, resolution and mounting height affect image quality.',
      cameraNoteDetail: 'First possible camera position – plan a narrower viewing angle for detail targets.',
      cameraNoteCheck: 'First possible camera position – please check and adjust.',
      cameraSuggestion: 'Suggestion {{count}}',
      uncoveredPartial: 'partial',
      uncoveredInsufficient: 'not sufficient',
      toastNoPositions: 'No new positions found. Add surveillance targets or rooms.',
      toastSuggested: '{{count}} first possible camera position(s) suggested.',
    },
  },
};